// platform/src/components/mw/MwMockups.tsx
//
// Mockups visuales del producto dentro de MwProductTile en la landing.
// Datos verídicos extraídos del diagnóstico y los módulos reales.
// Tokens: styles/mw.css.

import { useEffect, useState } from 'react'
import { Check } from 'lucide-react'

// ─── MOCKUP 1: DIAGNÓSTICO (animado, cicla preguntas reales) ─────────
type DiagSnapshot = {
  step: string
  question: string
  options: string[]
  selectedIdx: number
}

// Preguntas reales del diagnostic.yaml (Q1, Q2, Q3)
const DIAGNOSTIC_SNAPSHOTS: DiagSnapshot[] = [
  {
    step: 'Pregunta 1 de 10',
    question: '¿Cómo describirías tu situación laboral actual?',
    options: [
      'Asalariado con contrato estable',
      'Autónomo o freelance',
      'Desempleado o en transición',
    ],
    selectedIdx: 0,
  },
  {
    step: 'Pregunta 2 de 10',
    question: '¿Tienes deudas activas (excluyendo hipoteca)?',
    options: [
      'No, ninguna',
      'Alguna pequeña',
      'Sí, una cantidad relevante',
    ],
    selectedIdx: 0,
  },
  {
    step: 'Pregunta 3 de 10',
    question: '¿Cómo describirías tu relación con la inversión?',
    options: [
      'No invierto, solo ahorro',
      'Empiezo ahora a invertir',
      'Tengo cartera diversificada',
    ],
    selectedIdx: 1,
  },
]

export function MwMockupDiagnostic() {
  const [idx, setIdx] = useState(0)
  const [fading, setFading] = useState(false)

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) return

    const interval = setInterval(() => {
      setFading(true)
      setTimeout(() => {
        setIdx((i) => (i + 1) % DIAGNOSTIC_SNAPSHOTS.length)
        setFading(false)
      }, 250)
    }, 4500)
    return () => clearInterval(interval)
  }, [])

  const snap = DIAGNOSTIC_SNAPSHOTS[idx]

  return (
    <div
      className="rounded-2xl p-4 md:p-5"
      style={{
        background: 'var(--mw-surface-section)',
        border: '1px solid var(--mw-border)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <p
          style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--mw-ink-muted)',
            fontFamily: 'var(--mw-font-body)',
            margin: 0,
            opacity: fading ? 0 : 1,
            transition: 'opacity 250ms var(--mw-ease)',
          }}
        >
          {snap.step}
        </p>
        <div className="flex gap-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <span
              key={i}
              className="rounded-full"
              style={{
                width: '14px',
                height: '3px',
                background: i <= idx ? 'var(--mw-accent)' : 'var(--mw-border)',
                transition: 'background-color var(--mw-duration-medium) var(--mw-ease)',
              }}
            />
          ))}
        </div>
      </div>

      <p
        style={{
          fontFamily: 'var(--mw-font-display)',
          fontSize: '0.9375rem',
          fontWeight: 600,
          color: 'var(--mw-ink)',
          lineHeight: 1.3,
          margin: '0 0 0.875rem',
          opacity: fading ? 0 : 1,
          transition: 'opacity 250ms var(--mw-ease)',
          minHeight: '2.4em',
        }}
      >
        {snap.question}
      </p>

      <div className="flex flex-col gap-1.5" style={{ opacity: fading ? 0 : 1, transition: 'opacity 250ms var(--mw-ease)' }}>
        {snap.options.map((opt, i) => {
          const selected = i === snap.selectedIdx
          return (
            <div
              key={i}
              className="rounded-lg px-3 py-2 flex items-center justify-between gap-2"
              style={{
                border: selected ? '1.5px solid var(--mw-accent)' : '1px solid var(--mw-border)',
                background: selected ? 'var(--mw-accent-soft)' : 'transparent',
                color: 'var(--mw-ink)',
                fontSize: '12px',
                fontWeight: selected ? 500 : 400,
                fontFamily: 'var(--mw-font-body)',
              }}
            >
              <span>{opt}</span>
              {selected && (
                <Check size={14} style={{ color: 'var(--mw-accent)', flexShrink: 0 }} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── MOCKUP 2: TARJETA DE PERFIL (con datos actualizados) ────────────
export function MwMockupProfile() {
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: 'var(--mw-surface-inverse)',
        color: 'var(--mw-ink-inverse)',
      }}
    >
      <p
        style={{
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--mw-ink-inverse-muted)',
          fontFamily: 'var(--mw-font-body)',
          margin: 0,
        }}
      >
        Tu perfil
      </p>
      <p
        style={{
          fontFamily: 'var(--mw-font-display)',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--mw-ink-inverse)',
          letterSpacing: '-0.02em',
          margin: '0.5rem 0 0',
          lineHeight: 1.1,
        }}
      >
        Ahorrador metódico
      </p>
      <p
        style={{
          fontSize: '12px',
          color: 'var(--mw-ink-inverse-muted)',
          lineHeight: 1.5,
          margin: '0.625rem 0 0',
          fontFamily: 'var(--mw-font-body)',
        }}
      >
        Ahorras con disciplina. El siguiente paso es poner ese dinero a trabajar.
      </p>
      <div
        className="flex gap-4 mt-3 pt-3"
        style={{ borderTop: '1px solid var(--mw-border-inverse)' }}
      >
        <span style={{ fontSize: '11px', color: 'var(--mw-ink-inverse-muted)', fontFamily: 'var(--mw-font-body)' }}>
          Prioridad:{' '}
          <strong style={{ color: 'var(--mw-accent)', fontWeight: 600 }}>Inversión</strong>
        </span>
        <span style={{ fontSize: '11px', color: 'var(--mw-ink-inverse-muted)', fontFamily: 'var(--mw-font-body)' }}>
          Nivel:{' '}
          <strong style={{ color: 'var(--mw-ink-inverse)', fontWeight: 600 }}>Intermedio</strong>
        </span>
      </div>
    </div>
  )
}

// ─── MOCKUP 3: LISTA DE PÍLDORAS (títulos de módulos reales) ─────────
type Pill = { title: string; minutes: number; status: 'done' | 'current' | 'next' }

// Títulos reales de module.yaml
const PILLS: Pill[] = [
  { title: 'Tu radiografía financiera', minutes: 5, status: 'done' },
  { title: 'La regla 50/30/20', minutes: 5, status: 'done' },
  { title: 'Cuántos meses de fondo cubrir', minutes: 8, status: 'current' },
]

export function MwMockupPills() {
  return (
    <div className="flex flex-col gap-2">
      {PILLS.map((pill, i) => {
        const isDone = pill.status === 'done'
        const isCurrent = pill.status === 'current'
        return (
          <div
            key={i}
            className="rounded-xl px-3 py-2.5 flex items-center gap-2.5"
            style={{
              background: isCurrent ? 'var(--mw-surface)' : 'var(--mw-surface-section)',
              border: isCurrent ? '1.5px solid var(--mw-accent)' : '1px solid var(--mw-border)',
            }}
          >
            <span
              className="inline-flex items-center justify-center rounded-full"
              style={{
                width: '24px',
                height: '24px',
                background: isDone ? 'var(--mw-accent)' : 'var(--mw-accent-soft)',
                color: isDone ? 'var(--mw-ink-inverse)' : 'var(--mw-accent)',
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: 'var(--mw-font-body)',
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {isDone ? <Check size={13} /> : i + 1}
            </span>
            <span
              style={{
                fontSize: '13px',
                color: 'var(--mw-ink)',
                flex: 1,
                fontWeight: isCurrent ? 500 : 400,
                fontFamily: 'var(--mw-font-body)',
              }}
            >
              {pill.title}
            </span>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--mw-ink-muted)',
                fontFamily: 'var(--mw-font-body)',
                flexShrink: 0,
              }}
            >
              {pill.minutes} min
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ─── MOCKUP 4: CALCULADORA (Fondo de emergencia) ─────────────────────
export function MwMockupCalculator() {
  const target = 9450
  const current = 5860
  const pct = Math.round((current / target) * 100)

  return (
    <div
      className="rounded-2xl p-4 md:p-5"
      style={{
        background: 'var(--mw-surface-section)',
        border: '1px solid var(--mw-border)',
      }}
    >
      <p
        style={{
          fontSize: '11px',
          color: 'var(--mw-ink-muted)',
          fontFamily: 'var(--mw-font-body)',
          margin: 0,
        }}
      >
        Fondo de emergencia recomendado
      </p>
      <p
        style={{
          fontFamily: 'var(--mw-font-display)',
          fontSize: '2rem',
          fontWeight: 700,
          color: 'var(--mw-ink)',
          letterSpacing: '-0.025em',
          lineHeight: 1,
          margin: '0.25rem 0 0',
        }}
      >
        {target.toLocaleString('es-ES')} €
      </p>
      <p
        style={{
          fontSize: '11px',
          color: 'var(--mw-ink-muted)',
          fontFamily: 'var(--mw-font-body)',
          margin: '0.375rem 0 0.75rem',
        }}
      >
        Basado en 6 meses de gastos fijos
      </p>
      <div
        className="rounded-full overflow-hidden"
        style={{ background: 'var(--mw-border)', height: '4px' }}
      >
        <div
          className="rounded-full"
          style={{
            background: 'var(--mw-accent)',
            height: '100%',
            width: `${pct}%`,
            transition: 'width 1500ms var(--mw-ease)',
          }}
        />
      </div>
      <div className="flex justify-between mt-2">
        <span
          style={{
            fontSize: '11px',
            color: 'var(--mw-ink-secondary)',
            fontFamily: 'var(--mw-font-body)',
          }}
        >
          Tu ahorro actual
        </span>
        <span
          style={{
            fontSize: '11px',
            color: 'var(--mw-ink)',
            fontWeight: 600,
            fontFamily: 'var(--mw-font-body)',
          }}
        >
          {current.toLocaleString('es-ES')} € / {target.toLocaleString('es-ES')} €
        </span>
      </div>
    </div>
  )
}
